import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, Map, FileText, RefreshCcw, PauseCircle, Building2, UserCog, ScrollText, LogOut, Sprout, Upload, Bell, Wallet, Store, Receipt, Tag, Menu, BellRing, IdCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { fmtDate } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertSettingsDialog } from "@/components/app/AlertSettings";
import { PullToRefresh } from "@/components/app/PullToRefresh";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["super_admin", "admin"] },
  { to: "/plots", label: "Plots", icon: Map, roles: ["super_admin", "admin"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["super_admin", "admin"] },
  { to: "/payments", label: "Payments", icon: Wallet, roles: ["super_admin", "admin"] },
  { to: "/vendors", label: "Vendors", icon: Store, roles: ["super_admin", "admin"] },
  { to: "/expenses", label: "Expenses", icon: Receipt, roles: ["super_admin", "admin"] },
  { to: "/employees", label: "Employees", icon: IdCard, roles: ["super_admin", "admin"] },
  { to: "/plan-requests", label: "Plan Changes", icon: RefreshCcw, roles: ["super_admin", "admin"] },
  { to: "/pause-requests", label: "Pause Approvals", icon: PauseCircle, roles: ["super_admin", "admin"] },
  { to: "/imports", label: "Bulk Import", icon: Upload, roles: ["super_admin", "admin"] },
  { to: "/projects", label: "Projects & Rates", icon: Building2, roles: ["super_admin"] },
  { to: "/expense-categories", label: "Expense Categories", icon: Tag, roles: ["super_admin"] },
  { to: "/admins", label: "Admin Accounts", icon: UserCog, roles: ["super_admin"] },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["super_admin"] },
];

const brandName = (user) => user?.active_project?.company_name || user?.active_project?.name || "Sanjeevani";

function Brand({ user }) {
  return (
    <div className="px-6 py-6 flex items-center gap-2 border-b border-border">
      <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center">
        <Sprout className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="leading-tight">
        <div className="font-heading text-sm font-bold tracking-tight">{brandName(user)}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{user?.active_project ? "Project" : "Estates"}</div>
      </div>
    </div>
  );
}

function NavItems({ items, prefix = "nav", mobile = false, onNavigate }) {
  return items.map((n) => (
    <NavLink
      key={n.to}
      to={n.to}
      onClick={onNavigate}
      data-testid={`${prefix}-${n.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 ${mobile ? "py-3 text-[15px]" : "py-2 text-sm"} rounded-md transition-colors ${
          isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <n.icon className="h-4 w-4" strokeWidth={1.5} />
      {n.label}
    </NavLink>
  ));
}

function UserBlock({ user, logout, testId = "logout-btn" }) {
  return (
    <div className="p-4 border-t border-border">
      <div className="text-xs text-muted-foreground mb-1">Signed in as</div>
      <div className="text-sm font-medium truncate">{user.name}</div>
      <div className="text-xs text-muted-foreground truncate mb-3">{user.email} · {user.role.replace("_", " ")}</div>
      <Button data-testid={testId} variant="outline" size="sm" onClick={logout} className="w-full">
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

function NotifBell({ notif, loadNotif, markAllRead, onOpenAlerts, testId = "notif-bell" }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testId} variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" strokeWidth={1.5}/>
          {notif.unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-accent text-accent-foreground text-[9px] font-bold grid place-items-center">{notif.unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[calc(100vw-1.5rem)] p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="text-sm font-medium">Notifications</div>
          <div className="flex items-center gap-1">
            {notif.unread > 0 && <Button data-testid="notif-mark-all" size="sm" variant="ghost" onClick={markAllRead}>Mark all read</Button>}
            <Button data-testid={`${testId}-alerts`} size="sm" variant="ghost" onClick={onOpenAlerts} title="Phone alerts"><BellRing className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="max-h-80 overflow-auto">
          {notif.items.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">You're all caught up.</div>}
          {notif.items.map((n) => (
            <Link key={n.id} to={n.link || "/dashboard"} data-testid={`notif-item-${n.id}`}
                  onClick={async ()=>{ try{ await api.post(`/me/notifications/${n.id}/read`); loadNotif(); } catch{} }}
                  className={`block px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors ${n.read ? "" : "bg-accent/5"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-2 w-2 mt-1.5 rounded-full bg-accent shrink-0"/>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{fmtDate(n.created_at)}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MobileTopBar({ user, items, logout, bell, onOpenAlerts }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const current = items.find((n) => location.pathname.startsWith(n.to));
  return (
    <div className="md:hidden sticky top-0 z-30 h-14 flex items-center gap-2 px-2 border-b border-border bg-card/90 backdrop-blur-md pt-[env(safe-area-inset-top)] box-content">
      <Sheet open={open} onOpenChange={setOpen}>
        <Button data-testid="mobile-menu-btn" aria-label="Open menu" variant="ghost" size="icon" className="h-10 w-10" onClick={() => setOpen(true)}>
          <Menu className="h-5 w-5" />
        </Button>
        <SheetContent side="left" data-testid="mobile-nav-drawer" className="w-[84vw] max-w-[320px] p-0 flex flex-col gap-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Main menu</SheetDescription>
          <Brand user={user} />
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            <NavItems items={items} prefix="mnav" mobile onNavigate={() => setOpen(false)} />
            <button type="button" data-testid="mnav-alerts" onClick={() => { setOpen(false); onOpenAlerts(); }}
              className="w-full flex items-center gap-3 px-3 py-3 text-[15px] rounded-md text-foreground hover:bg-muted transition-colors">
              <BellRing className="h-4 w-4" strokeWidth={1.5} /> Phone alerts
            </button>
          </nav>
          <div className="pb-[env(safe-area-inset-bottom)]"><UserBlock user={user} logout={logout} testId="logout-btn-mobile" /></div>
        </SheetContent>
      </Sheet>
      <div className="min-w-0 flex-1 leading-tight">
        <div data-testid="mobile-page-title" className="font-heading font-bold text-[15px] truncate">{current?.label || brandName(user)}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{brandName(user)}</div>
      </div>
      {bell}
    </div>
  );
}

export default function AppShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user?.role === "customer") navigate("/portal");
  }, [user, loading, navigate]);

  const loadNotif = async () => {
    if (!user) return;
    try { const r = await api.get("/me/notifications", { params: { limit: 20 } }); setNotif(r.data); } catch {}
  };
  useEffect(() => { if (user) { loadNotif(); const iv = setInterval(loadNotif, 30000); return () => clearInterval(iv); } }, [user]);
  const markAllRead = async () => { try { await api.post("/me/notifications/read-all"); loadNotif(); } catch {} };

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  const items = nav.filter((n) => n.roles.includes(user.role));
  const bellProps = { notif, loadNotif, markAllRead, onOpenAlerts: () => setAlertsOpen(true) };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[260px_1fr] bg-background grain">
      <aside className="hidden md:flex flex-col border-r border-border bg-card">
        <Brand user={user} />
        <nav className="flex-1 px-3 py-6 space-y-1"><NavItems items={items} /></nav>
        <UserBlock user={user} logout={logout} />
      </aside>
      <main className="min-w-0">
        <MobileTopBar user={user} items={items} logout={logout} onOpenAlerts={() => setAlertsOpen(true)} bell={<NotifBell {...bellProps} testId="notif-bell-mobile" />} />
        {user?.impersonating && (
          <div data-testid="impersonation-banner" className="flex items-center justify-between gap-3 px-8 max-md:px-4 max-md:flex-wrap max-md:text-xs py-2 bg-amber-500 text-amber-950 text-sm">
            <div>
              <span className="font-semibold">Impersonating</span> ·
              You are inside <b>{user?.active_project?.company_name || user?.active_project?.name || "a project"}</b> as super admin ({user?.impersonated_by_email}). All actions are audited.
            </div>
            <button
              data-testid="exit-impersonation"
              onClick={async () => {
                try { await api.post("/auth/impersonate-exit"); } catch {}
                window.location.href = "https://" + ((process.env.REACT_APP_SUPER_ADMIN_HOST) || "app.sanjeevaniestates.com") + "/dashboard";
              }}
              className="underline underline-offset-2 hover:no-underline">
              Exit impersonation
            </button>
          </div>
        )}
        <div className="hidden md:flex items-center justify-end gap-3 px-8 py-3 border-b border-border bg-card">
          <NotifBell {...bellProps} />
        </div>
        <PullToRefresh onRefresh={() => { setRefreshKey((k) => k + 1); loadNotif(); }}>
          <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] max-md:pb-24">
            <Outlet key={refreshKey} />
          </div>
        </PullToRefresh>
        <AlertSettingsDialog open={alertsOpen} onOpenChange={setAlertsOpen} />
      </main>
    </div>
  );
}
